import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ChartPanel, Disclaimer, FadeIn, slug, tooltipStyle, axisProps } from "./common";
import { CategoryBackground } from "./SciBackground";
import CategoryHeader from "./CategoryHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  ALL,
  DEFAULT_FILTERS,
  PRODUCTS,
  RECORDS,
  applyFilters,
  getApplicableFailureModes,
  getApplicableMetrics,
  getProductApplicability,
  histogram,
  paretoL1,
  sanitizeFiltersForProduct,
} from "../data/synthetic";

const GRID = <CartesianGrid stroke="rgba(32,184,205,0.08)" vertical={false} />;
const WARN = "#FFB347";

function RcaSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-steel mb-1">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-navy border-teal/40 text-pale text-xs h-8 w-full" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-navy2 border-cyan/25 text-pale max-h-64">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="text-xs font-mono focus:bg-teal/30 focus:text-paper">
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const STEPS = [
  { n: "01", title: "Define", q: "What specifically failed? What is the expected state?", d: "A precise problem statement — product, stage, failure mode, magnitude — prevents solving the wrong problem." },
  { n: "02", title: "Contain", q: "Protect customer, product and process.", d: "Immediate actions stop further impact while the investigation proceeds; containment is temporary by design." },
  { n: "03", title: "Verify the Data", q: "Ensure the failure itself is real.", d: "Confirm the signal is not a data-handling, integration or instrument artifact before analyzing causes." },
  { n: "04", title: "Stratify", q: "Failure-mode stratification by product, instrument, lot, reagent, component, time, shift, QC stage, initial/retest and process parameter.", d: "Aggregate performance hides structure; stratification is where a process signature first concentrates." },
  { n: "05", title: "Build the Failure Picture", q: "Pareto, trend, distribution, failure concentration.", d: "Quantify the loss: magnitude, concentration, temporal correlation and deviation from expected behaviour." },
  { n: "06", title: "Generate Hypotheses", q: "5 Whys, Fishbone, FMEA, process knowledge, recent-change review, failure signatures.", d: "Structured generation across equipment, material/reagent, method, measurement and process-condition effects — no anchoring on the first plausible story." },
  { n: "07", title: "Test", q: "Controlled comparison, statistical evidence, retest behaviour, equipment and lot discrimination.", d: "Hypothesis discrimination: each candidate earns or loses credibility against evidence; eliminated paths are documented too." },
  { n: "08", title: "Verify Root Cause", q: "Require converging evidence.", d: "Root-cause verification needs independent lines of evidence agreeing — one correlation is not a cause." },
  { n: "09", title: "Implement Change", q: "CAPA, Deviation, Change Control, ECO, SOP/WI, Specification Review, Training, Process Change.", d: "In regulated manufacturing, improvement moves through controlled change — the Quality system is the delivery mechanism." },
  { n: "10", title: "Effectiveness", q: "FPY, scrap, recurrence, control-chart trend, complaints, failure rate.", d: "A closed CAPA is not proof; effectiveness monitoring over an appropriate period is." },
];

const TOOLS = [
  { name: "Pareto Analysis", when: "When several failure modes compete for attention.", how: "Rank failure modes by contribution so effort goes to the vital few, not the trivial many." },
  { name: "Fishbone / Ishikawa", when: "For structured hypothesis generation.", how: "Organize candidate causes across Machine, Material, Method, Measurement, Environment and People before testing any of them.", fishbone: true },
  { name: "5 Whys", when: "For causal-chain exploration.", how: "Follow the chain of 'why' past symptoms toward a controllable cause — stopping where evidence stops." },
  { name: "FMEA", when: "For risk-based process assessment.", how: "Score severity, occurrence and detectability to prioritize where controls and monitoring matter most." },
  { name: "Trend Analysis", when: "For time-dependent changes.", how: "Separate gradual process movement from sudden shifts and relate movement to change history." },
  { name: "Distribution Analysis", when: "For detecting population shifts.", how: "Compare shape, spread and centre — not just averages — between periods, instruments or lots." },
  { name: "Cp / Cpk", when: "For process capability assessment.", how: "Quantify whether the process spread and centering fit inside the specification window." },
  { name: "3σ Analysis", when: "For understanding natural process variation.", how: "Distinguish common-cause noise from signals worth investigating." },
  { name: "Correlation", when: "For testing association.", how: "Measure co-movement between factors — then treat it as a hypothesis, never as automatic causation." },
  { name: "Statistical Significance", when: "For evaluating population differences where assumptions are appropriate.", how: "Check whether an observed difference exceeds what sampling noise would plausibly produce." },
  { name: "Stratification", when: "For finding hidden signals within aggregate performance.", how: "Slice by instrument, lot, shift, stage and time until the pattern concentrates." },
  { name: "Retest Analysis", when: "For determining recovery patterns and distinguishing intermittent from persistent failures.", how: "High recovery suggests transient conditions; poor recovery suggests persistent state — each implies different causes." },
  { name: "Change-History Review", when: "For identifying relationships between process changes and performance.", how: "Align the timeline of changes against the timeline of performance movement." },
];

const FISHBONE = {
  Machine: ["Instrument wear state", "Pressure system drift", "Detector age"],
  Material: ["Reagent lot performance", "Component lot variation", "Storage excursions"],
  Method: ["Parameter setpoints", "Sample handling steps", "Integration settings"],
  Measurement: ["Calibration state", "Peak detection thresholds", "Reference marker logic"],
  Environment: ["Temperature cycling", "Relative humidity", "Vibration / placement"],
  People: ["Technique variation", "Training currency", "Shift handover gaps"],
};

const SCENARIO = [
  ["Verify data", "Confirm the 12% drop is real, not a reporting, integration or definition change."],
  ["Quantify impact", "Translate the drop into scrap, retests and time so prioritization is economic, not emotional."],
  ["Identify failure modes", "Determine which failure modes grew — a yield drop without mode detail is not yet investigable."],
  ["Pareto", "Rank modes by contribution to find the vital few driving the loss."],
  ["Stratify", "Slice by product, instrument, lot, shift and time to concentrate the signal."],
  ["Instrument analysis", "Test whether specific equipment carries the failure."],
  ["Lot analysis", "Test whether component lots correlate with the failures."],
  ["Reagent analysis", "Test whether reagent lots or designations shift the population."],
  ["Time analysis", "Locate when behaviour changed; gradual drift and step changes imply different causes."],
  ["Recent change review", "Align process, method, material or maintenance changes against the performance timeline."],
  ["Hypothesis generation", "Use Fishbone/5 Whys with process knowledge to build testable explanations."],
  ["Evidence testing", "Compare populations, run controlled checks, and let weak hypotheses fail early."],
  ["Containment", "Protect product and customers while the verified fix is prepared."],
  ["Root cause", "State the cause only where independent lines of evidence converge."],
  ["CAPA / process change", "Implement through controlled change so the improvement is documented and reversible."],
  ["Effectiveness monitoring", "Track FPY, recurrence and scrap over time — sustained performance closes the loop."],
];

function FishboneDiagram() {
  const [bone, setBone] = useState("Machine");
  return (
    <div data-testid="fishbone-diagram">
      <svg viewBox="0 0 640 220" className="w-full" role="img" aria-label="Fishbone diagram">
        <line x1="40" y1="110" x2="560" y2="110" stroke="#137C8B" strokeWidth="2" />
        <polygon points="560,96 610,110 560,124" fill="#0D1B2A" stroke="#20B8CD" strokeWidth="1.5" />
        <text x="575" y="114" fill="#20B8CD" fontSize="10" fontFamily="IBM Plex Mono" textAnchor="middle"></text>
        {Object.keys(FISHBONE).map((name, i) => {
          const top = i % 2 === 0;
          const x = 120 + Math.floor(i / 2) * 150;
          const y = top ? 40 : 180;
          const active = bone === name;
          return (
            <g key={name} onClick={() => setBone(name)} style={{ cursor: "pointer" }} data-testid={`fishbone-${name.toLowerCase()}`}>
              <rect x={x - 115} y={top ? y - 24 : 108} width={130} height={top ? 134 - y : y + 20 - 108} fill="#000" fillOpacity={0} pointerEvents="all" />
              <line x1={x} y1={top ? 110 : 110} x2={x - 50} y2={y} stroke={active ? "#20B8CD" : "#137C8B"} strokeWidth={active ? 2.5 : 1.5} />
              <text x={x - 56} y={top ? y - 6 : y + 14} fill={active ? "#20B8CD" : "#8A99A8"} fontSize="11" fontFamily="IBM Plex Mono" textAnchor="end">
                {name}
              </text>
            </g>
          );
        })}
        <text x="600" y="90" fill="#8A99A8" fontSize="9" fontFamily="IBM Plex Mono" textAnchor="middle">Effect</text>
      </svg>
      <div className="panel p-3 mt-2">
        <p className="font-mono text-xs text-cyan">{bone} — example hypothesis space</p>
        <ul className="mt-2 space-y-1">
          {FISHBONE[bone].map((c) => (
            <li key={c} className="text-xs text-pale/80 font-body">• {c}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function RcaLab() {
  const [tool, setTool] = useState(TOOLS[0]);
  const [step, setStep] = useState(0);
  const [context, setContext] = useState({ product: ALL, failureMode: ALL, metric: ALL });

  const failureModeOptions = useMemo(() => getApplicableFailureModes(context.product), [context.product]);
  const metricOptions = useMemo(() => getApplicableMetrics(context.product), [context.product]);
  const applicability = useMemo(() => getProductApplicability(context.product), [context.product]);
  const selectedMetric = metricOptions.find((m) => m.key === context.metric) || metricOptions[0];
  const productRecords = useMemo(
    () => applyFilters(RECORDS, { ...DEFAULT_FILTERS, product: context.product }),
    [context.product]
  );
  const focusRecords = useMemo(
    () => applyFilters(RECORDS, { ...DEFAULT_FILTERS, product: context.product, failureMode: context.failureMode }),
    [context.product, context.failureMode]
  );
  const modePareto = useMemo(() => paretoL1(productRecords).slice(0, 8), [productRecords]);
  const metricDistribution = useMemo(
    () => (selectedMetric ? histogram(focusRecords, selectedMetric) : []),
    [focusRecords, selectedMetric]
  );

  const updateProduct = (product) => setContext((c) => sanitizeFiltersForProduct({ ...c, product }));
  return (
    <section id="rca-lab" className="relative overflow-hidden scroll-mt-16" data-testid="rca-lab">
      <CategoryBackground variant="pareto" accent="warn" accent2="critical" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CategoryHeader
        id="rca-lab"
        category="rca-lab"
        eyebrow="RCA Lab"
        title="How I Approach Root Cause Analysis"
        description="Hypothesis-driven investigation using failure-mode stratification, evidence mapping and root-cause verification. A ten-stage scientific discipline — from a precise problem statement to verified, sustained correction — powers every investigation case study on this site."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
        {STEPS.map((s, i) => (
          <FadeIn key={s.n} delay={(i % 5) * 0.05}>
            <div className="panel p-4 h-full hover:border-cyan/40 transition-colors" data-testid={`rca-step-${s.n}`}>
              <p className="font-mono text-cyan text-xs">{s.n}</p>
              <h3 className="font-sans text-sm font-semibold text-paper mt-2 uppercase tracking-wide">{s.title}</h3>
              <p className="font-mono text-[10px] text-teal mt-2 leading-relaxed">{s.q}</p>
              <p className="text-xs text-steel mt-2 leading-relaxed">{s.d}</p>
            </div>
          </FadeIn>
        ))}
      </div>

      <div className="mt-20">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Interactive RCA Toolbox</h3>
        <p className="text-steel text-sm mt-2">Select a tool to see when it earns its place in an investigation.</p>
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6 mt-8">
          <div className="grid sm:grid-cols-2 gap-2 content-start" data-testid="rca-toolbox-list">
            {TOOLS.map((t) => (
              <button
                key={t.name}
                data-testid={`rca-tool-${slug(t.name)}`}
                onClick={() => setTool(t)}
                className={`text-left p-3 rounded-sm border transition-colors ${
                  tool.name === t.name ? "border-cyan bg-cyan/10" : "border-teal/30 bg-navy2 hover:border-cyan/50"
                }`}
              >
                <p className={`font-sans text-sm font-semibold ${tool.name === t.name ? "text-cyan" : "text-paper"}`}>{t.name}</p>
                <p className="text-[11px] text-steel mt-1 leading-snug">{t.when}</p>
              </button>
            ))}
          </div>
          <div className="panel p-6" data-testid="rca-tool-detail">
            <p className="overline-label">Selected Tool</p>
            <h4 className="font-sans text-xl font-semibold text-paper mt-2">{tool.name}</h4>
            <p className="text-sm text-cyan/90 mt-1 font-body">{tool.when}</p>
            <p className="text-sm text-pale/80 mt-4 leading-relaxed">{tool.how}</p>
            {tool.fishbone && <div className="mt-4"><FishboneDiagram /></div>}
          </div>
        </div>
      </div>

      <div className="mt-20" data-testid="rca-product-context">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Product-Aware RCA Evidence Explorer</h3>
        <p className="text-steel text-sm mt-2 max-w-3xl leading-relaxed">
          A fictional applicability layer narrows the failure modes and quantitative measures that belong in scope for each synthetic product,
          mirroring how a governed analytics model prevents impossible investigation states.
        </p>
        <div className="panel p-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <RcaSelect label="Synthetic Product" value={context.product} onChange={updateProduct} options={[ALL, ...PRODUCTS]} testId="rca-product-select" />
            <RcaSelect label="Failure Mode" value={context.failureMode} onChange={(v) => setContext((c) => ({ ...c, failureMode: v }))} options={[ALL, ...failureModeOptions]} testId="rca-failure-mode-select" />
            <RcaSelect label="Metric" value={context.metric} onChange={(v) => setContext((c) => ({ ...c, metric: v }))} options={[ALL, ...metricOptions.map((m) => m.key)]} testId="rca-metric-select" />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mt-3">
            <p className="text-xs text-pale/75 leading-relaxed" data-testid="rca-applicability-note">
              {context.product === ALL
                ? "All fictional product families are in scope."
                : `${context.product}: ${failureModeOptions.length} applicable failure modes and ${metricOptions.length} metric views.`}{" "}
              {applicability.rationale}
            </p>
            <button
              data-testid="rca-reset-context"
              onClick={() => setContext({ product: ALL, failureMode: ALL, metric: ALL })}
              className="self-start lg:self-auto font-mono text-[11px] text-cyan hover:text-paper transition-colors"
            >
              Reset RCA context
            </button>
          </div>
        </div>
        <div className="mt-4 max-w-3xl">
          <Disclaimer>Fictional applicability mapping only — it demonstrates state management and data-model governance without representing a real product control strategy.</Disclaimer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
          <ChartPanel
            title={`Applicable Failure Pareto — ${context.product === ALL ? "All Products" : context.product}`}
            subtitle="click a bar to focus the RCA context"
            testId="rca-pareto-chart"
            action={context.failureMode !== ALL && (
              <button className="font-mono text-[10px] text-warn" onClick={() => setContext((c) => ({ ...c, failureMode: ALL }))} data-testid="rca-clear-failure-filter">
                Clear: {context.failureMode}
              </button>
            )}
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={modePareto} margin={{ left: -14, right: 8, top: 4 }}>
                {GRID}
                <XAxis dataKey="mode" {...axisProps} interval={0} angle={-32} textAnchor="end" height={86} />
                <YAxis {...axisProps} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" name="Failures" cursor="pointer" onClick={(d) => d && d.mode && setContext((c) => ({ ...c, failureMode: d.mode }))}>
                  {modePareto.map((e) => (
                    <Cell key={e.mode} fill={e.mode === context.failureMode ? WARN : "#20B8CD"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>

          <ChartPanel
            title={`${selectedMetric?.label || "Metric"} Distribution`}
            subtitle={`${context.failureMode === ALL ? "All applicable failure modes" : context.failureMode} • ${selectedMetric?.unit || "synthetic"}`}
            testId="rca-metric-distribution-chart"
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={metricDistribution} margin={{ left: -18, right: 8, top: 4 }}>
                {GRID}
                <XAxis dataKey="bin" {...axisProps} interval={5} />
                <YAxis {...axisProps} />
                <Tooltip {...tooltipStyle} labelFormatter={(v) => `${selectedMetric?.label}: ${v}`} formatter={(v) => [v, "Runs"]} />
                <Bar dataKey="count" name="Runs" fill="#7C6FE5" />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </div>
      </div>

      <div className="mt-20 panel p-6 lg:p-8" data-testid="how-i-think-scenario">
        <p className="overline-label">How I Think — Interactive Scenario</p>
        <h3 className="font-sans tracking-tight text-2xl text-paper mt-3">Manufacturing yield suddenly decreases by 12%. Where do you start?</h3>
        <div className="mt-6">
          <div className="h-1 bg-navy2 rounded-full overflow-hidden" aria-hidden="true">
            <div className="h-full bg-cyan transition-all duration-500" style={{ width: `${((step + 1) / SCENARIO.length) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="font-mono text-xs text-steel">
              Step {step + 1} / {SCENARIO.length}
            </p>
            <div className="flex gap-2">
              <button
                data-testid="scenario-prev"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-1 font-mono text-xs px-3 py-1.5 border border-teal/40 rounded-sm text-pale/80 hover:border-cyan/60 disabled:opacity-40"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button
                data-testid="scenario-next"
                onClick={() => setStep((s) => Math.min(SCENARIO.length - 1, s + 1))}
                disabled={step === SCENARIO.length - 1}
                className="flex items-center gap-1 font-mono text-xs px-3 py-1.5 bg-cyan text-navy rounded-sm hover:bg-pale disabled:opacity-40"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-[220px_1fr] gap-4 items-start" data-testid="scenario-step-detail">
            <div className="panel p-4 border-l-2 border-l-cyan">
              <p className="font-sans text-lg font-semibold text-cyan capitalize">{SCENARIO[step][0]}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Why this step</p>
              <p className="text-sm text-pale/85 mt-2 leading-relaxed">{SCENARIO[step][1]}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-5">
            {SCENARIO.map(([t], i) => (
              <button
                key={t}
                data-testid={`scenario-dot-${i}`}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}: ${t}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-cyan" : i < step ? "w-3 bg-teal" : "w-3 bg-navy2 border border-teal/40"}`}
              />
            ))}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
